"use strict";
/**
 * Copyright 2023 actions-toolkit authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Install = void 0;
const fs_1 = __importDefault(require("fs"));
const os_1 = __importDefault(require("os"));
const path_1 = __importDefault(require("path"));
const core = __importStar(require("@actions/core"));
const httpm = __importStar(require("@actions/http-client"));
const tc = __importStar(require("@actions/tool-cache"));
const semver = __importStar(require("semver"));
const util = __importStar(require("util"));
const buildx_1 = require("./buildx");
const context_1 = require("../context");
const exec_1 = require("../exec");
const docker_1 = require("../docker");
const git_1 = require("../git");
class Install {
    constructor(opts) {
        this._standalone = opts === null || opts === void 0 ? void 0 : opts.standalone;
    }
    download(version) {
        return __awaiter(this, void 0, void 0, function* () {
            const release = yield Install.getRelease(version);
            const fversion = release.tag_name.replace(/^v+|v+$/g, '');
            core.debug(`Install.download version: ${fversion}`);
            let toolPath;
            toolPath = tc.find('buildx', fversion, this.platform());
            if (!toolPath) {
                const c = semver.clean(fversion) || '';
                if (!semver.valid(c)) {
                    throw new Error(`Invalid Buildx version "${fversion}".`);
                }
                toolPath = yield this.fetchBinary(fversion);
            }
            core.debug(`Install.download toolPath: ${toolPath}`);
            return toolPath;
        });
    }
    build(gitContext) {
        return __awaiter(this, void 0, void 0, function* () {
            // eslint-disable-next-line prefer-const
            let [repo, ref] = gitContext.split('#');
            if (ref.length == 0) {
                ref = 'master';
            }
            let vspec;
            // TODO: include full ref as fingerprint. Use commit sha as best-effort in the meantime.
            if (ref.match(/^[0-9a-fA-F]{40}$/)) {
                vspec = ref;
            }
            else {
                vspec = yield git_1.Git.getRemoteSha(repo, ref);
            }
            core.debug(`Install.build: tool version spec ${vspec}`);
            let toolPath;
            toolPath = tc.find('buildx', vspec);
            if (!toolPath) {
                const outputDir = path_1.default.join(context_1.Context.tmpDir(), 'build-cache');
                const buildCmd = yield this.buildCommand(gitContext, outputDir);
                toolPath = yield exec_1.Exec.getExecOutput(buildCmd.command, buildCmd.args, {
                    ignoreReturnCode: true
                }).then(res => {
                    if (res.stderr.length > 0 && res.exitCode != 0) {
                        core.warning(res.stderr.trim());
                    }
                    return tc.cacheFile(`${outputDir}/buildx`, os_1.default.platform() == 'win32' ? 'docker-buildx.exe' : 'docker-buildx', 'buildx', vspec);
                });
            }
            return toolPath;
        });
    }
    installStandalone(toolPath, dest) {
        return __awaiter(this, void 0, void 0, function* () {
            core.info('Standalone mode');
            dest = dest || context_1.Context.tmpDir();
            const toolBinPath = path_1.default.join(toolPath, os_1.default.platform() == 'win32' ? 'docker-buildx.exe' : 'docker-buildx');
            const binDir = path_1.default.join(dest, 'bin');
            if (!fs_1.default.existsSync(binDir)) {
                fs_1.default.mkdirSync(binDir, { recursive: true });
            }
            const filename = os_1.default.platform() == 'win32' ? 'buildx.exe' : 'buildx';
            const buildxPath = path_1.default.join(binDir, filename);
            fs_1.default.copyFileSync(toolBinPath, buildxPath);
            core.info('Fixing perms');
            fs_1.default.chmodSync(buildxPath, '0755');
            core.addPath(binDir);
            core.info('Added Buildx to PATH');
            core.info(`Binary path: ${buildxPath}`);
            return buildxPath;
        });
    }
    installPlugin(toolPath, dest) {
        return __awaiter(this, void 0, void 0, function* () {
            core.info('Docker plugin mode');
            dest = dest || docker_1.Docker.configDir;
            const toolBinPath = path_1.default.join(toolPath, os_1.default.platform() == 'win32' ? 'docker-buildx.exe' : 'docker-buildx');
            const pluginsDir = path_1.default.join(dest, 'cli-plugins');
            if (!fs_1.default.existsSync(pluginsDir)) {
                fs_1.default.mkdirSync(pluginsDir, { recursive: true });
            }
            const filename = os_1.default.platform() == 'win32' ? 'docker-buildx.exe' : 'docker-buildx';
            const pluginPath = path_1.default.join(pluginsDir, filename);
            fs_1.default.copyFileSync(toolBinPath, pluginPath);
            core.info('Fixing perms');
            fs_1.default.chmodSync(pluginPath, '0755');
            core.info(`Plugin path: ${pluginPath}`);
            return pluginPath;
        });
    }
    buildCommand(gitContext, outputDir) {
        return __awaiter(this, void 0, void 0, function* () {
            const buildxStandaloneFound = yield new buildx_1.Buildx({ standalone: true }).isAvailable();
            const buildxPluginFound = yield new buildx_1.Buildx({ standalone: false }).isAvailable();
            let buildStandalone = false;
            if ((yield this.isStandalone()) && buildxStandaloneFound) {
                core.debug(`Install.buildCommand: Buildx standalone found, build with it`);
                buildStandalone = true;
            }
            else if (!(yield this.isStandalone()) && buildxPluginFound) {
                core.debug(`Install.buildCommand: Buildx plugin found, build with it`);
                buildStandalone = false;
            }
            else if (buildxStandaloneFound) {
                core.debug(`Install.buildCommand: Buildx plugin not found, but standalone found so trying to build with it`);
                buildStandalone = true;
            }
            else if (buildxPluginFound) {
                core.debug(`Install.buildCommand: Buildx standalone not found, but plugin found so trying to build with it`);
                buildStandalone = false;
            }
            else {
                throw new Error(`Neither buildx standalone or plugin have been found to build from ref ${gitContext}`);
            }
            //prettier-ignore
            return yield new buildx_1.Buildx({ standalone: buildStandalone }).getCommand([
                'build',
                '--target', 'binaries',
                '--build-arg', 'BUILDKIT_CONTEXT_KEEP_GIT_DIR=1',
                '--output', `type=local,dest=${outputDir}`,
                gitContext
            ]);
        });
    }
    isStandalone() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const standalone = (_a = this._standalone) !== null && _a !== void 0 ? _a : !(yield docker_1.Docker.isAvailable());
            core.debug(`Install.isStandalone: ${standalone}`);
            return standalone;
        });
    }
    fetchBinary(version) {
        return __awaiter(this, void 0, void 0, function* () {
            const targetFile = os_1.default.platform() == 'win32' ? 'docker-buildx.exe' : 'docker-buildx';
            const downloadURL = util.format('https://github.com/docker/buildx/releases/download/v%s/%s', version, this.filename(version));
            core.info(`Downloading ${downloadURL}`);
            const downloadPath = yield tc.downloadTool(downloadURL);
            core.debug(`Install.fetchBinary downloadPath: ${downloadPath}`);
            return yield tc.cacheFile(downloadPath, targetFile, 'buildx', version);
        });
    }
    platform() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const arm_version = process.config.variables.arm_version;
        return `${os_1.default.platform()}-${os_1.default.arch()}${arm_version ? 'v' + arm_version : ''}`;
    }
    filename(version) {
        let arch;
        switch (os_1.default.arch()) {
            case 'x64': {
                arch = 'amd64';
                break;
            }
            case 'ppc64': {
                arch = 'ppc64le';
                break;
            }
            case 'arm': {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const arm_version = process.config.variables.arm_version;
                arch = arm_version ? 'arm-v' + arm_version : 'arm';
                break;
            }
            default: {
                arch = os_1.default.arch();
                break;
            }
        }
        const platform = os_1.default.platform() == 'win32' ? 'windows' : os_1.default.platform();
        const ext = os_1.default.platform() == 'win32' ? '.exe' : '';
        return util.format('buildx-v%s.%s-%s%s', version, platform, arch, ext);
    }
    static getRelease(version) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `https://raw.githubusercontent.com/docker/actions-toolkit/main/.github/buildx-releases.json`;
            const http = new httpm.HttpClient('docker-actions-toolkit');
            const resp = yield http.get(url);
            const body = yield resp.readBody();
            const statusCode = resp.message.statusCode || 500;
            if (statusCode >= 400) {
                throw new Error(`Failed to get Buildx release ${version} from ${url} with status code ${statusCode}: ${body}`);
            }
            const releases = JSON.parse(body);
            if (!releases[version]) {
                throw new Error(`Cannot find Buildx release ${version} in ${url}`);
            }
            return releases[version];
        });
    }
}
exports.Install = Install;
//# sourceMappingURL=install.js.map